// ── Design decisions ──
// Panel lateral derecho (no modal) para analizar UNA VEZ la lista completa del
// remitente y convertirla en N paquetes con destinatario propio. Reutiliza el
// Drawer compartido del módulo viajero SIN la piel Ambiance: todo lo que se ve
// acá usa los tiles, badges y variables CSS del asistente de encomienda, para
// que la operadora de mostrador no note un cambio de mundo.
//
// · Tres rutas de entrada (Foto / PDF, Pegar texto, Excel) que desembocan en la
//   MISMA forma { extracted: { paquetes } }: el panel no sabe de qué ruta vino
//   cada grupo, solo lo muestra con su chip de procedencia (IA / Texto / Excel).
//   Texto y Excel se analizan en el dispositivo (cero red, cero costo); la foto
//   y el PDF viajan por supabase.functions.invoke a través del adaptador.
// · La guardia del PDF (10 MB) vive en el cliente con el MISMO mensaje que la
//   Edge Function, para no gastar la subida.
// · La lista del remitente es la fuente de verdad: el nombre leído queda como
//   provenance de solo lectura ("Leído: …") y el destinatario se sugiere desde
//   el banco del remitente con la regla conservadora de autoMatch (coincidencia
//   exacta e inequívoca; un empate nunca decide). Nunca se crea un cliente ni
//   una dirección desde acá. Los tags de referencia (36H, #116) no entran al
//   nombre.
// · Destino: el primer grupo cae en el paquete 1 si está vacío; el resto crea
//   paquetes nuevos. Un paquete vacío solo puede ser destino de un grupo.
// · Aplicar es idempotente: se aplica grupo por grupo, se recuerda qué grupos
//   ya se escribieron (y con qué paquete) y un reintento solo toca los que
//   fallaron. Si el paquete se creó pero sus artículos no, el grupo pasa a
//   destino "existente" con ese paquete para que el reintento no duplique. El
//   mismo contenido (content_hash / hash local) nunca se aplica dos veces en
//   la sesión.
// · Estados diseñados: inactivo, leyendo, analizando (con Cancelar por
//   contador de solicitud), resultado, resultado guardado (sin costo), error
//   (mensaje en español + Reintentar) y lista vacía. Las advertencias son
//   ámbar e informan; solo la eliminación de un grupo pide confirmación (rojo).
// · Teclado: Ctrl+Enter aplica, Esc cierra (lo maneja el Drawer). El onClose
//   que recibe el Drawer es estable por identidad (refs), porque el Drawer
//   re-enfoca su panel cada vez que cambia.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AlertTriangle, ChevronDown, ChevronRight, FileText, Loader, Plus, Trash2 } from 'lucide-react';
import Drawer from '../ViajeroEncomienda/shared/Drawer';
import IsNewToggle from '../ViajeroEncomienda/shared/IsNewToggle';
import { AutoAsignadoChip, ProvenanceChip, ReviewDot } from '../ViajeroEncomienda/shared/chips';
import { fmtUsd } from '../ViajeroEncomienda/shared/labels';
import {
  hashLocal,
  limpiarNombre,
  mapearExtraccionAGrupos,
  recipientDesdeAddress,
  recipientVacio,
  totalesGrupos,
} from './agencyMapper';
import { CAMPOS } from './parseListaExcel';

// Hashes (content_hash de foto/PDF, hash local de texto/Excel) ya aplicados en
// esta sesión del navegador: el mismo contenido nunca se escribe dos veces.
const aplicadosEnSesion = new Set();
export const _resetAplicados = () => aplicadosEnSesion.clear();

const TABS = [
  { id: 'archivo', label: 'Foto / PDF' },
  { id: 'texto', label: 'Pegar texto' },
  { id: 'excel', label: 'Excel' },
];

const ETIQUETA_CAMPO = {
  destinatario: 'Destinatario',
  cantidad: 'Cantidad',
  descripcion: 'Descripción',
  precio: 'Precio',
  condicion: 'Condición',
  unidad: 'Unidad',
};

const CHIP = 'inline-flex items-center whitespace-nowrap rounded-full border px-2 py-0.5 text-[10px] font-semibold leading-4';

const FOCO = 'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--blue)] focus-visible:ring-offset-1';

const num = (v) => {
  const n = parseFloat(v);
  return Number.isFinite(n) ? n : 0;
};

// Clave de dedup de respaldo para foto/PDF cuando la función no devolvió
// content_hash (p. ej. sin tenant_id): hash local de los bytes del archivo.
// La guardia "nunca aplicar dos veces" SIEMPRE tiene clave.
const hashArchivoLocal = (file) =>
  new Promise((resolve) => {
    const respaldo = () => resolve(hashLocal(`archivo:${file?.name || ''}:${file?.size || 0}`));
    try {
      const reader = new FileReader();
      reader.onload = () => {
        const res = String(reader.result || '');
        const coma = res.indexOf(',');
        resolve(hashLocal('archivo:' + (coma >= 0 ? res.slice(coma + 1) : res)));
      };
      reader.onerror = respaldo;
      reader.readAsDataURL(file);
    } catch (_) {
      respaldo();
    }
  });

const tieneContenido = (it) =>
  Boolean(it?.id) || Boolean(String(it?.description ?? '').trim()) || num(it?.unitValue) > 0;

const etiquetaPaquete = (p) => `Paquete ${p.package_number} — ${p.recipient?.name || 'Sin destinatario'}`;

// Chip de procedencia: IA reutiliza el compartido; Texto/Excel/Manual se pintan
// acá con la misma píldora para no tocar el módulo viajero.
const FuenteChip = ({ source }) => {
  if (source === 'ai') return <ProvenanceChip source="ai" />;
  if (source === 'manual') return <ProvenanceChip source="manual" />;
  const texto = source === 'excel' ? 'Excel' : 'Texto';
  return (
    <span className={`${CHIP} border-slate-200 bg-slate-50 text-slate-600`} title={`Leído de ${texto.toLowerCase()} — análisis local`}>
      {texto}
    </span>
  );
};

// ── Panel de la fuente (foto / PDF / texto / hojas) ──────────────────────────
const FuentePane = ({ fuente }) => {
  const [colapsado, setColapsado] = useState(false);
  if (!fuente) return null;
  const nombres = { imagen: 'Foto de la lista', pdf: 'Documento PDF', texto: 'Texto de la lista', excel: 'Hojas del Excel' };
  return (
    <section aria-label="Fuente de la lista" className="tile w-full md:sticky md:top-0 md:w-2/5 md:self-start" style={{ padding: '10px' }}>
      <button
        type="button"
        onClick={() => setColapsado((c) => !c)}
        aria-expanded={!colapsado}
        aria-label={colapsado ? 'Mostrar la fuente' : 'Ocultar la fuente'}
        className={`flex w-full items-center gap-2 rounded-md px-1 py-1 text-left ${FOCO}`}
      >
        {colapsado ? <ChevronRight size={14} aria-hidden="true" /> : <ChevronDown size={14} aria-hidden="true" />}
        <span className="metric-label">{nombres[fuente.tipo] || nombres.imagen}</span>
      </button>
      {!colapsado && (
        <div className="mt-2" style={{ maxHeight: '60vh', overflow: 'auto' }}>
          {fuente.tipo === 'imagen' && fuente.url && (
            <img src={fuente.url} alt="Lista de encomienda subida" className="block w-full rounded-md" style={{ border: '1px solid var(--border)' }} />
          )}
          {fuente.tipo === 'imagen' && !fuente.url && (
            <p className="empty-state">Vista previa no disponible</p>
          )}
          {fuente.tipo === 'pdf' && (
            <div className="flex items-center gap-3 px-2 py-4">
              <FileText size={26} aria-hidden="true" style={{ color: 'var(--text-tertiary)' }} />
              <div className="min-w-0">
                <p className="truncate text-sm font-semibold" style={{ color: 'var(--text-main)' }}>{fuente.nombre || 'documento.pdf'}</p>
                <p className="metric-sub">PDF analizado — sin vista previa</p>
              </div>
            </div>
          )}
          {fuente.tipo === 'texto' && (
            <ol aria-label="Texto pegado de la lista" className="m-0 list-none p-0 font-mono text-[12px] leading-5">
              {String(fuente.texto ?? '').split(/\r\n|\r|\n/).map((l, i) => (
                <li key={i} className="flex gap-2 whitespace-pre-wrap break-words">
                  <span aria-hidden="true" className="w-6 shrink-0 select-none text-right" style={{ color: 'var(--text-tertiary)' }}>{i + 1}</span>
                  <span className="min-w-0">{l || ' '}</span>
                </li>
              ))}
            </ol>
          )}
          {fuente.tipo === 'excel' && (
            <ul aria-label="Hojas del archivo" className="m-0 list-none p-0 text-xs">
              {(fuente.hojas || []).map((h) => (
                <li key={h.nombre} className="row-item" style={{ padding: '6px 4px' }}>
                  <span className="font-semibold">{h.nombre}</span>
                  <span className="metric-sub" style={{ marginLeft: 8 }}>{(h.filas || []).length} filas</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </section>
  );
};

// ── Drawer ───────────────────────────────────────────────────────────────────
const AnalizarListaDrawer = ({
  open,
  onClose,
  tenantId = null,
  destinatarios = [],
  paquetes = [],
  api,
  onAplicarGrupo,
  onAplicado,
}) => {
  const [tab, setTab] = useState('archivo');
  const [estado, setEstado] = useState('idle'); // idle | leyendo | analizando | mapeo | resultado | vacio | error
  const [error, setError] = useState('');
  const [resultado, setResultado] = useState(null);
  const [fuente, setFuente] = useState(null);
  const [texto, setTexto] = useState('');
  const [hojas, setHojas] = useState(null);
  const [porHoja, setPorHoja] = useState([]);
  const [mapeos, setMapeos] = useState({});
  const [grupos, setGrupos] = useState([]);
  const [aplicando, setAplicando] = useState(false);
  const [resultadosAplicar, setResultadosAplicar] = useState({});
  const [confirmarEliminar, setConfirmarEliminar] = useState(null); // { key, moverA }
  const [ctaEnfocado, setCtaEnfocado] = useState(false);

  const solicitud = useRef(0);
  const contador = useRef(0);
  const creadosRef = useRef([]); // packageIds creados en esta sesión del panel
  const urlRef = useRef(null);
  const refs = useRef({});
  refs.current = { onClose, aplicando };

  // El Drawer re-enfoca su panel cuando cambia onClose: identidad estable.
  const cerrar = useCallback(() => {
    if (refs.current.aplicando) return;
    refs.current.onClose && refs.current.onClose();
  }, []);

  const limpiarUrl = () => {
    if (urlRef.current && typeof URL.revokeObjectURL === 'function') {
      try { URL.revokeObjectURL(urlRef.current); } catch (_) {}
    }
    urlRef.current = null;
  };

  // Al abrir: todo limpio.
  useEffect(() => {
    if (!open) return;
    solicitud.current += 1;
    setTab('archivo');
    setEstado('idle');
    setError('');
    setResultado(null);
    setFuente(null);
    setTexto('');
    setHojas(null);
    setPorHoja([]);
    setMapeos({});
    setGrupos([]);
    setAplicando(false);
    setResultadosAplicar({});
    setConfirmarEliminar(null);
    creadosRef.current = [];
    limpiarUrl();
  }, [open]);

  useEffect(() => () => limpiarUrl(), []);

  // Paquetes de la sesión sin artículos: candidatos a Destino.
  const paquetesVacios = useMemo(
    () => (paquetes || []).filter((p) => !(p.items || []).some(tieneContenido)),
    [paquetes]
  );

  const hash = resultado?.content_hash || null;
  const yaAplicado = Boolean(hash && aplicadosEnSesion.has(hash));

  // ── Construcción de grupos desde un resultado ─────────────────────────────
  const mostrarResultado = (res, source) => {
    const gs = mapearExtraccionAGrupos(res, { destinatarios, paquetesVacios, source });
    setResultado(res);
    setGrupos(gs);
    setResultadosAplicar({});
    setConfirmarEliminar(null);
    const conArticulos = gs.some((g) => (g.items || []).length > 0);
    setEstado(conArticulos ? 'resultado' : 'vacio');
  };

  const fallar = (msg) => {
    setError(msg || 'No se pudo analizar la lista — intentá de nuevo.');
    setEstado('error');
  };

  // ── Ruta foto / PDF ───────────────────────────────────────────────────────
  const ultimoArchivo = useRef(null);
  const analizarArchivo = async (file) => {
    if (!file) return;
    ultimoArchivo.current = file;
    const esPdf = file.type === 'application/pdf' || /\.pdf$/i.test(file.name || '');
    limpiarUrl();
    if (esPdf) {
      setFuente({ tipo: 'pdf', nombre: file.name });
    } else {
      let url = null;
      if (typeof URL.createObjectURL === 'function') {
        try { url = URL.createObjectURL(file); } catch (_) { url = null; }
      }
      urlRef.current = url;
      setFuente({ tipo: 'imagen', url, nombre: file.name });
    }
    if (esPdf && file.size > api.LIMITE_PDF_BYTES) {
      fallar(api.ERROR_PDF_GRANDE);
      return;
    }
    const id = ++solicitud.current;
    setError('');
    setEstado('analizando');
    try {
      let res = await api.extractListaArchivo({ file, tenantId });
      if (id !== solicitud.current) return;
      if (!res?.content_hash) res = { ...res, content_hash: await hashArchivoLocal(file) };
      if (id !== solicitud.current) return;
      mostrarResultado(res, 'ai');
    } catch (err) {
      if (id !== solicitud.current) return;
      fallar(err?.message);
    }
  };

  const cancelar = () => {
    solicitud.current += 1;
    setEstado('idle');
  };

  // ── Ruta texto pegado ─────────────────────────────────────────────────────
  const analizarTextoPegado = () => {
    if (!String(texto).trim()) return;
    limpiarUrl();
    setFuente({ tipo: 'texto', texto });
    setError('');
    try {
      const res = api.analizarTexto(texto);
      mostrarResultado(res, 'texto');
    } catch (err) {
      fallar(err?.message);
    }
  };

  // ── Ruta Excel ────────────────────────────────────────────────────────────
  const aplicarHojas = (hs, mps) => {
    const r = api.analizarExcelHojas(hs, { mapeos: mps });
    setPorHoja(r.porHoja || []);
    if (r.necesitaMapeo) {
      // Mapeo inicial: lo detectado, para que la operadora solo complete lo que falta.
      setMapeos((prev) => {
        const next = { ...prev };
        (r.porHoja || []).forEach((p) => {
          if (!p.resultado.necesitaMapeo) return;
          if (!next[p.nombre]) {
            const base = {};
            CAMPOS.forEach((k) => { base[k] = p.resultado.columnas?.[k] ?? ''; });
            next[p.nombre] = base;
          }
        });
        return next;
      });
      setEstado('mapeo');
      return;
    }
    mostrarResultado(r.resultado, 'excel');
  };

  const analizarExcel = async (file) => {
    if (!file) return;
    limpiarUrl();
    const id = ++solicitud.current;
    setError('');
    setEstado('leyendo');
    try {
      const hs = await api.leerExcel(file);
      if (id !== solicitud.current) return;
      setHojas(hs);
      setMapeos({});
      setFuente({ tipo: 'excel', hojas: hs, nombre: file.name });
      aplicarHojas(hs, {});
    } catch (err) {
      if (id !== solicitud.current) return;
      fallar(err?.message);
    }
  };

  const continuarMapeo = () => {
    if (!hojas) return;
    const mps = {};
    Object.entries(mapeos).forEach(([hoja, campos]) => {
      const m = {};
      CAMPOS.forEach((k) => {
        const v = campos?.[k];
        m[k] = v === '' || v === undefined || v === null ? null : Number(v);
      });
      mps[hoja] = m;
    });
    try {
      aplicarHojas(hojas, mps);
    } catch (err) {
      fallar(err?.message);
    }
  };

  const reintentarAnalisis = () => {
    if (tab === 'archivo' && ultimoArchivo.current) return analizarArchivo(ultimoArchivo.current);
    if (tab === 'texto') return analizarTextoPegado();
    setEstado('idle');
    setError('');
    return undefined;
  };

  // ── Edición de grupos ─────────────────────────────────────────────────────
  const cambiarGrupo = (key, patch) =>
    setGrupos((prev) => prev.map((g) => (g.key === key ? { ...g, ...patch } : g)));

  const cambiarItem = (gKey, iKey, patch) =>
    setGrupos((prev) =>
      prev.map((g) =>
        g.key === gKey ? { ...g, items: g.items.map((it) => (it.key === iKey ? { ...it, ...patch } : it)) } : g
      )
    );

  const quitarItem = (gKey, iKey) =>
    setGrupos((prev) => prev.map((g) => (g.key === gKey ? { ...g, items: g.items.filter((it) => it.key !== iKey) } : g)));

  const nuevoItem = (gKey) => {
    contador.current += 1;
    return {
      key: `${gKey}-m-${contador.current}`,
      description_raw: '',
      qty: 1,
      unit_type: 'UNIDAD',
      unit_price: null,
      is_new: true,
      notes: null,
      requires_review: false,
      source: 'manual',
    };
  };

  const agregarItem = (gKey) =>
    setGrupos((prev) => prev.map((g) => (g.key === gKey ? { ...g, items: [...g.items, nuevoItem(gKey)] } : g)));

  const nuevoGrupo = () => {
    contador.current += 1;
    return {
      key: `g-m-${contador.current}`,
      owner_name_raw: '',
      source: 'manual',
      recipient: recipientVacio(),
      addressId: null,
      auto: false,
      destino: { tipo: 'nuevo' },
      requires_review: false,
      items: [],
    };
  };

  const agregarGrupo = () => setGrupos((prev) => [...prev, nuevoGrupo()]);

  // Mover un artículo a otro grupo o a un grupo nuevo.
  const moverItem = (gKey, iKey, destinoKey) => {
    setGrupos((prev) => {
      const origen = prev.find((g) => g.key === gKey);
      const item = origen?.items.find((it) => it.key === iKey);
      if (!item) return prev;
      const sinItem = prev.map((g) => (g.key === gKey ? { ...g, items: g.items.filter((it) => it.key !== iKey) } : g));
      if (destinoKey === 'nuevo') {
        const g = nuevoGrupo();
        return [...sinItem, { ...g, items: [item] }];
      }
      return sinItem.map((g) => (g.key === destinoKey ? { ...g, items: [...g.items, item] } : g));
    });
  };

  // Unir: los artículos de este grupo pasan al otro y este desaparece.
  const unirGrupo = (gKey, targetKey) => {
    if (!targetKey || targetKey === gKey) return;
    setGrupos((prev) => {
      const origen = prev.find((g) => g.key === gKey);
      if (!origen) return prev;
      return prev
        .filter((g) => g.key !== gKey)
        .map((g) => (g.key === targetKey ? { ...g, items: [...g.items, ...origen.items] } : g));
    });
  };

  const eliminarGrupo = (gKey, moverA) => {
    if (moverA) {
      unirGrupo(gKey, moverA);
    } else {
      setGrupos((prev) => prev.filter((g) => g.key !== gKey));
    }
    setConfirmarEliminar(null);
  };

  const elegirDestinatario = (g, valor) => {
    if (valor === 'libre') {
      cambiarGrupo(g.key, { recipient: { ...recipientVacio(), name: limpiarNombre(g.owner_name_raw) }, addressId: null, auto: false });
      return;
    }
    const addr = (destinatarios || []).find((a) => String(a.id) === String(valor));
    if (!addr) return;
    cambiarGrupo(g.key, { recipient: recipientDesdeAddress(addr), addressId: addr.id, auto: false });
  };

  // ── Aplicar (idempotente, grupo por grupo) ────────────────────────────────
  // Un grupo sin artículos nunca se aplica: no crea paquete (ni nuevo ni
  // existente), no cuenta en el CTA y se avisa en ámbar. Se puede completar a
  // mano o eliminar antes de aplicar.
  const gruposConItems = grupos.filter((g) => (g.items || []).length > 0);
  const gruposVacios = grupos.length - gruposConItems.length;
  const totales = totalesGrupos(gruposConItems);
  const gruposPendientes = gruposConItems.filter((g) => !resultadosAplicar[g.key]?.ok);
  const hayErrores = gruposConItems.some((g) => resultadosAplicar[g.key] && !resultadosAplicar[g.key].ok);
  const puedeAplicar = gruposConItems.length > 0 && !aplicando && !yaAplicado && gruposPendientes.length > 0;

  const gruposRef = useRef(grupos);
  gruposRef.current = grupos;
  const paquetesRef = useRef(paquetes);
  paquetesRef.current = paquetes;

  const aplicar = async () => {
    if (!puedeAplicar) return;
    setAplicando(true);
    const lista = gruposRef.current.map((g) => ({ ...g }));
    const previos = { ...resultadosAplicar };
    const resultados = { ...previos };
    // Paquetes creados por este panel que el padre todavía no muestra: ocupan número.
    const sesion = paquetesRef.current || [];
    let creadosOcultos = creadosRef.current.filter((id) => !sesion.some((p) => p.id === id)).length;

    for (const g of lista) {
      if ((g.items || []).length === 0) continue; // nunca un paquete vacío
      if (resultados[g.key]?.ok) continue;
      const packageNumber = sesion.length + 1 + creadosOcultos;
      try {
        const r = await onAplicarGrupo(g, { packageNumber });
        if (g.destino?.tipo === 'nuevo') {
          creadosOcultos += 1;
          if (r?.packageId) creadosRef.current.push(r.packageId);
        }
        resultados[g.key] = { ok: true, packageId: r?.packageId ?? g.destino?.packageId ?? null };
      } catch (err) {
        if (err?.packageId) {
          // El paquete existe: el reintento solo escribe los artículos.
          g.destino = { tipo: 'existente', packageId: err.packageId };
          creadosOcultos += 1;
          creadosRef.current.push(err.packageId);
        }
        resultados[g.key] = { ok: false, error: err?.message || 'No se pudo escribir el paquete.', packageId: err?.packageId ?? null };
      }
    }

    setGrupos(lista);
    setResultadosAplicar(resultados);
    try {
      if (onAplicado) await onAplicado({ resultados });
    } catch (_) {
      // El rehidratado del padre no bloquea el panel.
    }
    const todoOk = lista.every((g) => (g.items || []).length === 0 || resultados[g.key]?.ok);
    setAplicando(false);
    if (todoOk) {
      if (hash) aplicadosEnSesion.add(hash);
      refs.current.onClose && refs.current.onClose();
    }
  };

  const aplicarRef = useRef(aplicar);
  aplicarRef.current = aplicar;

  // Ctrl+Enter aplica mientras el panel está abierto.
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.ctrlKey && e.key === 'Enter') {
        e.preventDefault();
        aplicarRef.current();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open]);

  // ── Render ────────────────────────────────────────────────────────────────
  const notas = resultado?.extracted?.extraction_notes;
  const esCached = Boolean(resultado?.cached);
  const hojasPendientes = porHoja.filter((p) => p.resultado?.necesitaMapeo);

  const footer = (
    <div className="flex flex-col gap-2">
      {yaAplicado && (
        <p role="status" className="badge badge-hold w-full" style={{ padding: '6px 10px', whiteSpace: 'normal' }}>
          Esta lista ya se aplicó en esta sesión.
        </p>
      )}
      {gruposVacios > 0 && (
        <p role="status" className="badge badge-hold w-full" style={{ padding: '6px 10px', whiteSpace: 'normal' }} data-testid="aviso-vacios">
          {gruposVacios} grupo{gruposVacios === 1 ? '' : 's'} sin artículos no se aplicar{gruposVacios === 1 ? 'á' : 'án'} — agregá artículos o eliminalos.
        </p>
      )}
      {hayErrores && !aplicando && (
        <p role="alert" className="badge badge-error w-full" style={{ padding: '6px 10px', whiteSpace: 'normal' }}>
          Algunos paquetes no se pudieron escribir. Revisá los avisos y usá Reintentar: solo se vuelven a escribir los que fallaron.
        </p>
      )}
      <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2">
        <p className="text-xs" style={{ color: 'var(--text-secondary)', margin: 0 }} aria-live="polite" data-testid="totales">
          <span className="font-semibold" style={{ color: 'var(--text-main)' }}>{totales.paquetes} paquetes</span>
          {' · '}{totales.articulos} artículos
          {' · '}{totales.porRevisar} por revisar
          {' · '}FOB <span className="font-mono font-semibold" style={{ color: 'var(--green)' }}>${fmtUsd(totales.fob)}</span>
        </p>
        <div className="flex items-center gap-2">
          <button type="button" onClick={cerrar} disabled={aplicando} className={`btn btn-secondary ${FOCO}`} aria-label="Cerrar sin aplicar">
            Cerrar
          </button>
          <button
            type="button"
            onClick={aplicar}
            disabled={!puedeAplicar}
            onFocus={() => setCtaEnfocado(true)}
            onBlur={() => setCtaEnfocado(false)}
            title={`Aplicar (Ctrl+Enter)`}
            aria-label={hayErrores ? 'Reintentar los paquetes que fallaron (Ctrl+Enter)' : `Aplicar ${totales.paquetes} paquetes y ${totales.articulos} artículos (Ctrl+Enter)`}
            className={`btn btn-primary ${FOCO}`}
            style={{ minHeight: '44px', justifyContent: 'center' }}
          >
            {aplicando && <Loader size={14} className="animate-spin mr-2" aria-hidden="true" />}
            {aplicando
              ? 'Aplicando…'
              : hayErrores
              ? `Reintentar ${gruposPendientes.length} paquete${gruposPendientes.length === 1 ? '' : 's'}`
              : `Aplicar ${totales.paquetes} paquete${totales.paquetes === 1 ? '' : 's'} · ${totales.articulos} artículo${totales.articulos === 1 ? '' : 's'}`}
            {ctaEnfocado && (
              <kbd className="ml-2 rounded border px-1 text-[9px] font-normal" style={{ borderColor: 'rgba(255,255,255,0.5)' }}>Ctrl+Enter</kbd>
            )}
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <Drawer open={open} title="Analizar lista del cliente" onClose={cerrar} width="xl" footer={footer}>
      {/* Pestañas de entrada */}
      <div role="tablist" aria-label="Tipo de lista" className="mb-3 flex gap-1" style={{ borderBottom: '1px solid var(--border)' }}>
        {TABS.map((t) => (
          <button
            key={t.id}
            type="button"
            role="tab"
            aria-selected={tab === t.id}
            aria-controls={`panel-${t.id}`}
            id={`tab-${t.id}`}
            onClick={() => setTab(t.id)}
            disabled={aplicando}
            className={`px-3 py-2 text-xs font-semibold ${FOCO}`}
            style={{
              minHeight: '44px',
              color: tab === t.id ? 'var(--avianca-red)' : 'var(--text-secondary)',
              borderBottom: tab === t.id ? '2px solid var(--avianca-red)' : '2px solid transparent',
              background: 'transparent',
            }}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Entrada por pestaña */}
      <div className="mb-3 flex flex-wrap items-center gap-2" role="tabpanel" id={`panel-${tab}`} aria-labelledby={`tab-${tab}`}>
        {tab === 'archivo' && (
          <label className={`btn btn-secondary cursor-pointer ${FOCO}`} style={{ minHeight: '44px' }}>
            📎 Elegir foto o PDF
            <input
              type="file"
              className="hidden"
              accept="image/*,application/pdf"
              aria-label="Archivo de la lista (foto o PDF)"
              disabled={aplicando || estado === 'analizando'}
              onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ''; analizarArchivo(f); }}
            />
          </label>
        )}
        {tab === 'texto' && (
          <div className="flex w-full flex-col gap-2">
            <textarea
              className="input"
              rows={5}
              aria-label="Texto de la lista"
              placeholder="Pegá acá la lista que mandó el cliente por WhatsApp…"
              value={texto}
              onChange={(e) => setTexto(e.target.value)}
              disabled={aplicando}
            />
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={analizarTextoPegado}
                disabled={aplicando || !String(texto).trim()}
                className={`btn btn-primary ${FOCO}`}
                style={{ minHeight: '44px' }}
                aria-label="Analizar el texto pegado"
              >
                Analizar texto
              </button>
              <span className={`${CHIP} border-slate-200 bg-slate-50 text-slate-600`} title="Se analiza en este dispositivo, sin llamar a la IA">
                Análisis local — sin costo
              </span>
            </div>
          </div>
        )}
        {tab === 'excel' && (
          <label className={`btn btn-secondary cursor-pointer ${FOCO}`} style={{ minHeight: '44px' }}>
            📊 Elegir archivo de Excel
            <input
              type="file"
              className="hidden"
              accept=".xlsx,.xls,.csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
              aria-label="Archivo de Excel con la lista"
              disabled={aplicando || estado === 'leyendo'}
              onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ''; analizarExcel(f); }}
            />
          </label>
        )}
      </div>

      {/* Estados */}
      {estado === 'idle' && (
        <p className="empty-state">
          Todavía no hay lista analizada. Subí una foto o un PDF, pegá el texto o elegí un Excel.
        </p>
      )}
      {estado === 'leyendo' && (
        <p role="status" className="flex items-center gap-2 text-xs" style={{ color: 'var(--text-secondary)' }}>
          <Loader size={13} className="animate-spin" aria-hidden="true" /> Leyendo el archivo…
        </p>
      )}
      {estado === 'analizando' && (
        <div role="status" className="tile flex items-center justify-between gap-3" style={{ padding: '12px' }}>
          <span className="flex items-center gap-2 text-xs" style={{ color: 'var(--text-secondary)' }}>
            <Loader size={13} className="animate-spin" aria-hidden="true" /> Analizando la lista…
          </span>
          <button type="button" onClick={cancelar} className={`btn btn-ghost ${FOCO}`} aria-label="Cancelar el análisis">
            Cancelar
          </button>
        </div>
      )}
      {estado === 'error' && (
        <div className="tile" style={{ padding: '12px', borderColor: 'var(--avianca-red)', background: 'var(--red-soft)' }}>
          <p role="alert" className="text-xs font-medium" style={{ color: 'var(--avianca-red)', margin: 0 }}>{error}</p>
          {fuente?.nombre && <p className="metric-sub mt-1">{fuente.nombre}</p>}
          <button type="button" onClick={reintentarAnalisis} className={`btn btn-secondary mt-2 ${FOCO}`} aria-label="Reintentar el análisis">
            Reintentar
          </button>
        </div>
      )}
      {estado === 'vacio' && (
        <div className="empty-state" role="status">
          No encontramos artículos en la lista. Revisá la foto o agregá los artículos a mano.
          <div className="mt-2">
            <button type="button" onClick={() => { agregarGrupo(); setEstado('resultado'); }} className={`btn btn-secondary ${FOCO}`}>
              <Plus size={12} aria-hidden="true" /> Agregar paquete a mano
            </button>
          </div>
        </div>
      )}

      {/* Mapeo de columnas del Excel */}
      {estado === 'mapeo' && (
        <div className="tile space-y-3" style={{ padding: '12px' }} aria-label="Mapeo de columnas">
          <p className="text-xs" style={{ color: 'var(--text-secondary)', margin: 0 }}>
            No reconocimos los encabezados de {hojasPendientes.length === 1 ? 'una hoja' : `${hojasPendientes.length} hojas`}. Indicá qué columna es cada campo (Descripción es obligatoria).
          </p>
          {hojasPendientes.map((p) => (
            <div key={p.nombre} className="space-y-2">
              <p className="metric-label">Hoja "{p.nombre}"</p>
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                {CAMPOS.map((campo) => (
                  <label key={campo} className="flex flex-col gap-1 text-[10px]" style={{ color: 'var(--text-secondary)' }}>
                    {ETIQUETA_CAMPO[campo]}{campo === 'descripcion' ? ' *' : ''}
                    <select
                      className="input"
                      aria-label={`Columna de ${ETIQUETA_CAMPO[campo].toLowerCase()} (hoja ${p.nombre})`}
                      value={mapeos[p.nombre]?.[campo] ?? ''}
                      onChange={(e) => setMapeos((prev) => ({ ...prev, [p.nombre]: { ...(prev[p.nombre] || {}), [campo]: e.target.value } }))}
                    >
                      <option value="">— ninguna —</option>
                      {(p.resultado.encabezados || []).map((h, i) => (
                        <option key={i} value={i}>{h || `Columna ${i + 1}`}</option>
                      ))}
                    </select>
                  </label>
                ))}
              </div>
            </div>
          ))}
          <button type="button" onClick={continuarMapeo} className={`btn btn-primary ${FOCO}`} style={{ minHeight: '44px' }} aria-label="Continuar con el mapeo de columnas">
            Continuar
          </button>
        </div>
      )}

      {/* Resultado */}
      {(estado === 'resultado' || (estado === 'vacio' && grupos.length > 0)) && (
        <>
          {(notas || esCached) && (
            <div role="status" className="mb-3 flex items-start gap-2 rounded-md px-3 py-2 text-xs" style={{ background: 'var(--amber-soft)', border: '1px solid var(--amber)', color: 'var(--amber)' }}>
              <AlertTriangle size={14} className="mt-0.5 shrink-0" aria-hidden="true" />
              <div className="min-w-0 flex-1">
                <p className="font-semibold" style={{ margin: 0 }}>Lo leído es una propuesta: revisá cada grupo antes de aplicar.</p>
                {notas && <p style={{ margin: '2px 0 0' }}>{notas}</p>}
              </div>
              {esCached && (
                <span className={`${CHIP} shrink-0 border-slate-200 bg-white text-slate-600`} title="Este archivo ya se había analizado: se reutilizó el resultado guardado">
                  Resultado guardado — sin costo
                </span>
              )}
            </div>
          )}

          <div className="flex min-h-0 flex-col gap-3 md:flex-row md:items-start md:gap-4">
            <FuentePane fuente={fuente} />

            <section aria-label="Paquetes propuestos" className="flex min-w-0 flex-1 flex-col gap-3">
              {grupos.map((g, gi) => {
                const res = resultadosAplicar[g.key];
                const otros = grupos.filter((x) => x.key !== g.key);
                const destinosTomados = new Set(
                  grupos.filter((x) => x.key !== g.key && x.destino?.tipo === 'existente').map((x) => x.destino.packageId)
                );
                const selectDestinatario = g.addressId ? String(g.addressId) : 'libre';
                const bloqueado = aplicando || Boolean(res?.ok);
                return (
                  <article
                    key={g.key}
                    aria-label={`Paquete propuesto ${gi + 1}`}
                    className="tile"
                    style={{ padding: '12px', borderColor: res?.ok ? 'var(--green)' : res && !res.ok ? 'var(--avianca-red)' : undefined }}
                  >
                    <header className="mb-2 flex flex-wrap items-center gap-2">
                      <span className="badge badge-enc">Grupo {gi + 1}</span>
                      <FuenteChip source={g.source} />
                      {g.owner_name_raw ? (
                        <span className="text-xs" style={{ color: 'var(--text-secondary)' }} title="Nombre tal como aparece en la lista">
                          Leído: <span className="font-semibold" style={{ color: 'var(--text-main)' }}>{g.owner_name_raw}</span>
                        </span>
                      ) : (
                        <span className="text-xs" style={{ color: 'var(--text-tertiary)' }}>Sin nombre en la lista</span>
                      )}
                      {g.requires_review && <ReviewDot visible />}
                      {res?.ok && <span className="badge badge-done">Aplicado</span>}
                      <span className="ml-auto text-xs font-mono font-semibold" style={{ color: 'var(--green)' }}>
                        ${fmtUsd(totalesGrupos([g]).fob)}
                      </span>
                    </header>

                    {res && !res.ok && (
                      <p role="alert" className="mb-2 text-xs font-medium" style={{ color: 'var(--avianca-red)' }}>
                        {res.error}
                      </p>
                    )}

                    <div className="mb-2 grid grid-cols-1 gap-2 sm:grid-cols-2">
                      <div className="flex flex-col gap-1">
                        <label className="metric-label" htmlFor={`dest-${g.key}`}>Destinatario</label>
                        <div className="flex items-center gap-2">
                          <select
                            id={`dest-${g.key}`}
                            className="input"
                            aria-label="Destinatario"
                            value={selectDestinatario}
                            disabled={bloqueado}
                            onChange={(e) => elegirDestinatario(g, e.target.value)}
                          >
                            <option value="libre">Nuevo destinatario (texto libre)</option>
                            {(destinatarios || []).map((a) => (
                              <option key={a.id} value={String(a.id)}>
                                {a.label ? `${a.label} — ` : ''}{a.recipient_name}
                              </option>
                            ))}
                          </select>
                          {g.auto && g.addressId && (
                            <span className="flex items-center gap-1 shrink-0">
                              <AutoAsignadoChip />
                              <span className="text-[10px]" style={{ color: 'var(--text-tertiary)' }}>auto</span>
                            </span>
                          )}
                        </div>
                        {!g.addressId && (
                          <input
                            className="input"
                            type="text"
                            aria-label="Nombre del destinatario nuevo"
                            placeholder="Nombre del destinatario"
                            value={g.recipient?.name || ''}
                            disabled={bloqueado}
                            onChange={(e) => cambiarGrupo(g.key, { recipient: { ...g.recipient, name: e.target.value }, auto: false })}
                          />
                        )}
                      </div>
                      <div className="flex flex-col gap-1">
                        <label className="metric-label" htmlFor={`destino-${g.key}`}>Destino</label>
                        <select
                          id={`destino-${g.key}`}
                          className="input"
                          aria-label="Destino"
                          value={g.destino?.tipo === 'existente' ? String(g.destino.packageId) : 'nuevo'}
                          disabled={bloqueado}
                          onChange={(e) =>
                            cambiarGrupo(g.key, {
                              destino: e.target.value === 'nuevo' ? { tipo: 'nuevo' } : { tipo: 'existente', packageId: e.target.value },
                            })
                          }
                        >
                          <option value="nuevo">Paquete nuevo</option>
                          {paquetesVacios.map((p) => (
                            <option key={p.id} value={String(p.id)} disabled={destinosTomados.has(p.id)}>
                              {etiquetaPaquete(p)}{destinosTomados.has(p.id) ? ' (ya elegido)' : ''}
                            </option>
                          ))}
                          {g.destino?.tipo === 'existente' && !paquetesVacios.some((p) => p.id === g.destino.packageId) && (
                            <option value={String(g.destino.packageId)}>Paquete existente</option>
                          )}
                        </select>
                      </div>
                    </div>

                    {/* Artículos */}
                    {g.items.length === 0 ? (
                      <p className="empty-state" style={{ padding: '8px 0' }}>
                        Este grupo quedó sin artículos: agregá uno a mano o eliminá el paquete.
                      </p>
                    ) : (
                      <ul aria-label={`Artículos del grupo ${gi + 1}`} className="m-0 flex list-none flex-col gap-2 p-0">
                        {g.items.map((it) => {
                          const sinPrecio = it.unit_price === null || it.unit_price === undefined || it.unit_price === '';
                          const flag = Boolean(it.requires_review) || sinPrecio;
                          return (
                            <li
                              key={it.key}
                              aria-label={`Artículo ${it.description_raw || 'sin descripción'}`}
                              className="rounded-md p-2"
                              style={{ border: '1px solid var(--border)', background: flag ? 'var(--amber-soft)' : 'var(--bg)' }}
                            >
                              <div className="grid grid-cols-[52px_1fr] gap-2 sm:grid-cols-[52px_1fr_auto_88px]">
                                <input
                                  type="number"
                                  min={0}
                                  step={0.01}
                                  className="input text-center"
                                  style={{ padding: '4px' }}
                                  aria-label="Cantidad"
                                  value={it.qty}
                                  disabled={bloqueado}
                                  onChange={(e) => cambiarItem(g.key, it.key, { qty: e.target.value })}
                                />
                                <input
                                  type="text"
                                  className="input"
                                  style={{ padding: '4px 8px' }}
                                  aria-label="Descripción"
                                  placeholder="Descripción del artículo"
                                  value={it.description_raw}
                                  disabled={bloqueado}
                                  onChange={(e) => cambiarItem(g.key, it.key, { description_raw: e.target.value })}
                                />
                                <IsNewToggle value={it.is_new} onChange={(is_new) => cambiarItem(g.key, it.key, { is_new })} disabled={bloqueado} />
                                <input
                                  type="number"
                                  min={0}
                                  step={0.01}
                                  className="input text-right"
                                  style={{ padding: '4px' }}
                                  aria-label="Valor unitario"
                                  placeholder="0.00"
                                  value={sinPrecio ? '' : it.unit_price}
                                  disabled={bloqueado}
                                  onChange={(e) =>
                                    cambiarItem(g.key, it.key, { unit_price: e.target.value === '' ? null : e.target.value })
                                  }
                                />
                              </div>
                              <div className="mt-1 flex flex-wrap items-center gap-2 text-[10px]" style={{ color: 'var(--text-secondary)' }}>
                                <ReviewDot visible={flag} />
                                {sinPrecio && <span style={{ color: 'var(--amber)' }}>Sin precio en la lista</span>}
                                {it.unit_type && it.unit_type !== 'UNIDAD' && <span className="badge badge-cas">{it.unit_type}</span>}
                                {it.notes && <span>{it.notes}</span>}
                                <FuenteChip source={it.source || g.source} />
                                <select
                                  className="input ml-auto"
                                  style={{ width: 'auto', padding: '2px 6px', fontSize: '10px' }}
                                  aria-label="Mover a paquete"
                                  value=""
                                  disabled={bloqueado}
                                  onChange={(e) => { const v = e.target.value; if (v) moverItem(g.key, it.key, v); }}
                                >
                                  <option value="">Mover a paquete…</option>
                                  {otros.map((o) => (
                                    <option key={o.key} value={o.key}>
                                      Grupo {grupos.indexOf(o) + 1}{o.recipient?.name ? ` — ${o.recipient.name}` : o.owner_name_raw ? ` — ${o.owner_name_raw}` : ''}
                                    </option>
                                  ))}
                                  <option value="nuevo">Nuevo paquete</option>
                                </select>
                                <button
                                  type="button"
                                  onClick={() => quitarItem(g.key, it.key)}
                                  disabled={bloqueado}
                                  aria-label="Quitar artículo"
                                  title="Quitar artículo"
                                  className={`btn-ghost ${FOCO}`}
                                  style={{ padding: '4px', color: 'var(--avianca-red)' }}
                                >
                                  <Trash2 size={13} aria-hidden="true" />
                                </button>
                              </div>
                            </li>
                          );
                        })}
                      </ul>
                    )}

                    <div className="mt-2 flex flex-wrap items-center gap-2">
                      <button type="button" onClick={() => agregarItem(g.key)} disabled={bloqueado} className={`btn btn-ghost ${FOCO}`} style={{ color: 'var(--avianca-red)', fontWeight: 'bold' }} aria-label={`Agregar artículo al grupo ${gi + 1}`}>
                        <Plus size={12} aria-hidden="true" /> Agregar artículo
                      </button>
                      <select
                        className="input"
                        style={{ width: 'auto', padding: '4px 8px', fontSize: '10px' }}
                        aria-label="Unir con"
                        value=""
                        disabled={bloqueado || otros.length === 0}
                        onChange={(e) => { const v = e.target.value; if (v) unirGrupo(g.key, v); }}
                      >
                        <option value="">Unir con…</option>
                        {otros.map((o) => (
                          <option key={o.key} value={o.key}>
                            Grupo {grupos.indexOf(o) + 1}{o.recipient?.name ? ` — ${o.recipient.name}` : ''}
                          </option>
                        ))}
                      </select>
                      <button
                        type="button"
                        onClick={() => setConfirmarEliminar({ key: g.key, moverA: otros[0]?.key || '' })}
                        disabled={bloqueado}
                        className={`btn btn-ghost ml-auto ${FOCO}`}
                        style={{ color: 'var(--avianca-red)' }}
                        aria-label={`Eliminar paquete propuesto ${gi + 1}`}
                      >
                        <Trash2 size={12} aria-hidden="true" /> Eliminar paquete
                      </button>
                    </div>

                    {confirmarEliminar?.key === g.key && (
                      <div role="alertdialog" aria-label="Confirmar eliminación del paquete" className="mt-2 rounded-md p-2 text-xs" style={{ background: 'var(--red-soft)', border: '1px solid var(--avianca-red)' }}>
                        <p className="font-semibold" style={{ color: 'var(--avianca-red)', margin: '0 0 6px' }}>
                          ¿Qué hacemos con los {g.items.length} artículos de este grupo?
                        </p>
                        <div className="flex flex-wrap items-center gap-2">
                          {otros.length > 0 && (
                            <>
                              <select
                                className="input"
                                style={{ width: 'auto', padding: '4px 8px' }}
                                aria-label="Mover artículos al grupo"
                                value={confirmarEliminar.moverA}
                                onChange={(e) => setConfirmarEliminar({ key: g.key, moverA: e.target.value })}
                              >
                                {otros.map((o) => (
                                  <option key={o.key} value={o.key}>Grupo {grupos.indexOf(o) + 1}{o.recipient?.name ? ` — ${o.recipient.name}` : ''}</option>
                                ))}
                              </select>
                              <button type="button" onClick={() => eliminarGrupo(g.key, confirmarEliminar.moverA)} className={`btn btn-secondary ${FOCO}`} aria-label="Mover artículos y eliminar el grupo">
                                Mover artículos a…
                              </button>
                            </>
                          )}
                          <button type="button" onClick={() => eliminarGrupo(g.key, null)} className={`btn btn-primary ${FOCO}`} style={{ background: 'var(--avianca-red)' }} aria-label="Eliminar el grupo y sus artículos">
                            Eliminar artículos
                          </button>
                          <button type="button" onClick={() => setConfirmarEliminar(null)} className={`btn btn-ghost ${FOCO}`} aria-label="No eliminar el grupo">
                            Volver
                          </button>
                        </div>
                      </div>
                    )}
                  </article>
                );
              })}

              <button type="button" onClick={agregarGrupo} disabled={aplicando} className={`btn btn-secondary self-start ${FOCO}`} style={{ borderStyle: 'dashed', minHeight: '44px' }} aria-label="Agregar un paquete nuevo a mano">
                <Plus size={14} aria-hidden="true" /> Nuevo paquete
              </button>
            </section>
          </div>
        </>
      )}
    </Drawer>
  );
};

export default AnalizarListaDrawer;
